import { FieldArray, Form, Formik } from "formik";
import {
    Dialog,
    DialogActions,
    DialogContent,
    Box,
    Button,
    Grid,
    Typography,
    Divider,
    CardMedia,
    InputAdornment,
} from "@mui/material";
import AddIcon from "@mui/icons-material/Add";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import * as yup from "yup";
import { useEffect, useRef, useState } from "react";
import { MFTextField } from "../../../lib/formik";
import MFSelectField from "../../../lib/formik/SelectField";
import { TextDatePicker } from "../../onboarding/DatePickerWithTextFeild";
import { CountryCodesDropDown } from "../../commonComponents";
import { getDateValidation, preventSpecialCharacters } from "../../../utils/utilityFunctions";
import { futureAge, maxAge, isMinor } from "../../../utils/utilityFunctions";
import { emailRegex, individualPanRegex, phoneRegExp, stringRegex, nameWithNumberRegex } from "../../../utils/regex";
import { dataTypeMasters, DataMaster } from "../../../redux-store/types/api-types";
import { getCountryCodes } from "../../../utils/utilityFunctions";
import { CountryCode, isValidPhoneNumber } from 'libphonenumber-js';
import { LoadingButton } from "@mui/lab";
import { VerifyPanAadharAmlKycSection } from "../../onboarding/enterPAN";
import { Theme } from "@mui/material/styles";
import { eighteenYearsAgoDate, INDIVIDUAL_ONBOARDING_CHAR_LIMIT, IS_DOB_APPLICABLE_FOR_AMC, yearfor125years, } from "../../../utils/constant";
import UseRadioGroup from '../../../lib/formik/Radio';
import { createSchemaForNonIndianResident } from "../../../utils/schema";
import { getFormattedISTDate } from "../../../utils/date";
export interface ManualJointHolder {
    salutation: string;
    fullName: string;
    dob: string;
    pan: string;
    email: string;
    gender: string;
    placeOfBirth: string;
    countryOfBirth: string;
    grossAnnualIncome: string;
    politicallyExposedPersonStatus: string;
    emailPersonRelation: string;
    emailDeclarationOther?: string;
    mobile: string;
    phonePersonRelation: string;
    mobileDeclarationOther?: string;
    countryCode: string;
    countryNameAndCode: string;
    panVerificationToken?: string;
    applicantVerificationToken: string;
    panVerificationStatus?: JointHolderVerifyStatus;
    taxResidentOfAnyCountryOtherThanIndia?: boolean;
    taxCountryName?: string;
    taxID?: string;
    idType?: string;

}

interface ManualJointHolderFormValues {
    jointHolders: ManualJointHolder[];
}

export interface JointHolderVerifyStatus {
    panAddharCheck: boolean;
    kraCheck: boolean;
    amlCheck: boolean;
    ckycCheck: boolean;
}

type VerificationFailureType = 'aadhaar' | 'kra';

interface FreeTextPopupProps {
    open: boolean;
    onClose: () => void;
    onSave: (values: ManualJointHolder[]) => Promise<void> | void;
    onUpdate?: (values: ManualJointHolder[]) => Promise<void> | void;
    initialValues?: ManualJointHolder[];
    emailRelationOptions?: dataTypeMasters[];
    mobileRelationOptions?: dataTypeMasters[];
    onVerifyPan?: (
        index: number,
        holder: ManualJointHolder
    ) => Promise<{ token: string; status: JointHolderVerifyStatus } | undefined>;
    isBSEEnabled?: boolean;
    isSaving?: boolean;
    mode?: 'add' | 'view';
    dataMasters?: DataMaster;
    dataMasterLoading?: boolean;
    onboardingSubType?: string;
}

const InitialHolder: ManualJointHolder = {
    salutation: "Mr.",
    fullName: "",
    dob: "",
    gender: "",
    placeOfBirth: "",
    countryOfBirth: "",
    grossAnnualIncome: "",
    politicallyExposedPersonStatus: "",
    pan: "",
    email: "",
    emailPersonRelation: "Self",
    emailDeclarationOther: "",
    mobile: "",
    phonePersonRelation: "Self",
    mobileDeclarationOther: "",
    countryCode: '+91',
    countryNameAndCode: 'India: +91',
    applicantVerificationToken: "",
    taxResidentOfAnyCountryOtherThanIndia: false,
    taxCountryName: "",
    taxID: "",
    idType: ""
};

const DEFAULT_STATUS: JointHolderVerifyStatus = {
    panAddharCheck: false,
    kraCheck: false,
    amlCheck: false,
    ckycCheck: false,
};

const manualHolderSchema = yup.object({
    salutation: yup.string().required("Salutation is required"),
    fullName: yup.string().required("Full Name is required")
        .matches(nameWithNumberRegex, 'Special Characters except .\'-()& not allowed')
        .test('max-length', (value, context) => {
            const fieldValue = value ?? "";
            const { path, createError } = context;
            if (`${fieldValue}`.length > INDIVIDUAL_ONBOARDING_CHAR_LIMIT.PERSONAL_DETAILS.FULL_NAME) {
                return createError({
                    message: `Full Name should not be greater than ${INDIVIDUAL_ONBOARDING_CHAR_LIMIT.PERSONAL_DETAILS.FULL_NAME} characters`,
                    path,
                });
            }
            return true;
        }),
    dob: yup.string().nullable().test('dob', (value, context) => {
        const fieldValue = value === undefined ? '' : value;
        const { path, createError, parent } = context;
        const { isBeforeLimit: limit125yrsBefore, } = getDateValidation({ date: fieldValue ?? '', limitValue: yearfor125years });
        const { isAfterLimit: limit18yrsAfter } = getDateValidation({ date: fieldValue ?? '', limitValue: eighteenYearsAgoDate });

        if (IS_DOB_APPLICABLE_FOR_AMC) {
            if (!fieldValue) {
                return createError({
                    message: 'Date of Birth is required',
                    path,
                });
            }
            const date = new Date(fieldValue);

            if (isNaN(date.getTime())) {
                return createError({ path, message: 'Invalid Date format' });
            }
            if (futureAge(fieldValue || '')) {
                return createError({
                    message: 'Future date of birth is not allowed',
                    path,
                });
            }
            if ((limit125yrsBefore) && parent.pan?.split('')[3] === 'P') {
                return createError({
                    message: 'Age should be less than 125 years',
                    path,
                });
            }
            if((limit18yrsAfter)&& parent.pan?.split('')[3]==='P'){
                return createError({
                    message:'Age should be greater than 18 years'
                })
            }
        }
        return true;
    }),
    pan: yup.string().matches(individualPanRegex, "Only Individual PANs are allowed").required("PAN is required"),
    email: yup.string().matches(emailRegex, "Invalid Email").required("Email is required"),
    countryNameAndCode: yup.string().test('countryNameAndCode', 'Invalid country selection', function (value) {
        const { createError, path, parent } = this;

        if (!value) {
            return createError({
                message: 'Country code is required',
                path,
            });
        }
        const codesList = getCountryCodes()
            .map((codes) => {
                if (codes.label === value) {
                    return codes.countryCode;
                }
            })
            .filter((ele) => ele)
            .toString();
        if (parent.mobile && !isValidPhoneNumber(parent.mobile, codesList as CountryCode)) {
            return createError({
                message: 'Invalid Mobile number',
                path,
            });
        }

        return true;
    }).required("Country code is required"),
    emailPersonRelation: yup.string().required("Select email declaration"),
    emailDeclarationOther: yup.string().when("emailDeclaration", {
        is: (value: string) => `${value ?? ""}`.toLowerCase() === "others",
        then: schema => schema.required("Please specify relationship"),
        otherwise: schema => schema.notRequired(),
    }),
    mobile: yup.string().test('mobile', 'Invalid mobile number', function (value) {
        const { createError, path, parent } = this;

        if (!value) {
            return createError({
                message: 'Mobile number is required',
                path,
            });
        }
        const codesList = getCountryCodes()
            .map((codes) => {
                if (codes.label === parent.countryNameAndCode) {
                    return codes.countryCode;
                }
            })
            .filter((ele) => ele)
            .toString();
        if (parent.countryNameAndCode === 'India: +91' && !phoneRegExp.test(value)) {
            return createError({
                message: 'Invalid mobile number',
                path,
            });
        }
        if (!isValidPhoneNumber(value, codesList as CountryCode)) {
            return createError({
                message: 'Invalid mobile number',
                path,
            });
        }

        return true;
    }).required("Mobile number is required"),
    phonePersonRelation: yup.string().required("Select mobile declaration"),
    mobileDeclarationOther: yup.string().when("mobileDeclaration", {
        is: (value: string) => `${value ?? ""}`.toLowerCase() === "others",
        then: schema => schema.required("Please specify relationship"),
        otherwise: schema => schema.notRequired(),
    }),
    gender: yup.string().nullable().required('Please select gender'),
    placeOfBirth: yup.string().required('Please enter place of birth').matches(stringRegex, 'Special characters and numbers not allowed'),
    countryOfBirth: yup.string().nullable().required('Country Of Birth is required'),
    grossAnnualIncome: yup.string().nullable().required('Gross Annual Income is required'),
    politicallyExposedPersonStatus: yup.string().required('PEP status is required'),
    // taxResidentOfAnyCountryOtherThanIndia: yup.boolean(),
    taxCountryName: yup
        .string()
        .when(
            'taxResidentOfAnyCountryOtherThanIndia',
            createSchemaForNonIndianResident('Please enter tax country name')
        ).test(
            'taxCountryName',
            (value, context) => {
                const fieldValue = value === null ? '' : value;
                const { createError, path, parent } = context;
                if (`${parent?.taxResidentOfAnyCountryOtherThanIndia}` == 'true' && !fieldValue) {
                    return createError({
                        message: 'Tax country name is required',
                        path,
                    });
                }
                if (`${parent?.taxResidentOfAnyCountryOtherThanIndia}` == 'true' && `${fieldValue ?? ''}`.toLowerCase() === 'india') {
                    return createError({
                        message: 'Tax country cannot be India',
                        path,
                    })
                }
                return true;
            }),
    taxID: yup
        .string()
        .max(20, 'Invalid Tax ID')
        .when(
            'taxResidentOfAnyCountryOtherThanIndia',
            createSchemaForNonIndianResident('Please enter Tax ID')
        ),
    idType: yup
        .string()
        .when(
            'taxResidentOfAnyCountryOtherThanIndia',
            createSchemaForNonIndianResident('Please enter ID Type')
        ),
});

const validationSchema = yup.object({
    jointHolders: yup.array().of(manualHolderSchema).min(1, "At least one joint holder is required"),
});

const createHolderSnapshot = (holder: ManualJointHolder): string => {
    return [
        holder.salutation?.trim() ?? "",
        holder.fullName?.trim() ?? "",
        holder.dob ?? "",
        holder.pan?.trim().toUpperCase() ?? "",
    ].join("|");
};

const FreeTextPopup = ({
    open,
    onClose,
    onSave,
    onUpdate,
    initialValues = [],
    emailRelationOptions = [],
    mobileRelationOptions = [],
    onVerifyPan,
    isBSEEnabled = false,
    isSaving = false,
    mode = 'add',
    dataMasters = {},
    dataMasterLoading = false,
    onboardingSubType = '',
}: FreeTextPopupProps): JSX.Element => {
    const {
        SALUTATION = [],
        INCOME_SLAB = [],
        GENDER = [],
        COUNTRY = [],
        PEP_STATUS = [],
        FATCA_ID_TYPE = []
    } = dataMasters ?? {};

    const [closeConfirmOpen, setCloseConfirmOpen] = useState(false);
    const formikDirtyRef = useRef(false);
    const [panStatus, setPanStatus] = useState<JointHolderVerifyStatus[]>([]);
    const [panVerifiedFlags, setPanVerifiedFlags] = useState<boolean[]>([]);
    const [panVerifyLoading, setPanVerifyLoading] = useState<boolean[]>([]);
    const [initialSnapshots, setInitialSnapshots] = useState<string[]>([]);
    const formikValuesRef = useRef<ManualJointHolderFormValues | null>(null);
    const formikRef = useRef<any>(null);
    const [fieldsEnabled, setFieldsEnabled] = useState<boolean[]>([]);
    const [initialEditableSnapshots, setInitialEditableSnapshots] = useState<string[]>([]);
    const [verificationFailedPopup, setVerificationFailedPopup] = useState<{
        open: boolean;
        message: string;
        type: VerificationFailureType;
        holderIndex?: number;
        pendingResult?: { token: string; status: JointHolderVerifyStatus };
    }>({ open: false, message: '', type: 'aadhaar' });
    const resetVerificationFailedPopup = () =>
        setVerificationFailedPopup({ open: false, message: '', type: 'aadhaar' });
    //this is for updating form in fe local store
    const createEditableSnapshot = (holder: ManualJointHolder | undefined) => {
        if (!holder) return '';
        return [
            holder.email ?? '',
            holder.emailPersonRelation ?? '',
            holder.emailDeclarationOther ?? '',
            holder.mobile ?? '',
            holder.phonePersonRelation ?? '',
            holder.mobileDeclarationOther ?? '',
            holder.grossAnnualIncome ?? '',
            holder.politicallyExposedPersonStatus ?? '',
            holder.taxResidentOfAnyCountryOtherThanIndia?.toString() ?? '',
            holder.taxCountryName ?? '',
            holder.taxID ?? '',
            holder.idType ?? '',
            holder.gender ?? '',
            holder.placeOfBirth ?? '',
            holder.countryOfBirth ?? '',
        ].join('|');
    };

    const applyManualHolderVerification = (holderIdx: number, token: string, status: JointHolderVerifyStatus) => {
        if (!formikRef.current) {
            return;
        }
        formikRef.current.setFieldValue(
            `jointHolders.${holderIdx}.panVerificationToken`,
            token,
            true
        );
        formikRef.current.setFieldValue(
            `jointHolders.${holderIdx}.panVerificationStatus`,
            status,
            true
        );
        setPanStatus((prev) => {
            const next = [...prev];
            next[holderIdx] = { ...status };
            return next;
        });
        setPanVerifiedFlags((prev) => {
            const next = [...prev];
            next[holderIdx] = true;
            return next;
        });
        setFieldsEnabled((prev) => {
            const next = [...prev];
            next[holderIdx] = true;
            return next;
        });
    };

    const handleVerificationFailureClose = () => {
        resetVerificationFailedPopup();
        if (formikRef.current) {
            formikRef.current.resetForm();
        }
        onClose();
    };

    const handleKraFailureProceed = () => {
        if (
            verificationFailedPopup.holderIndex === undefined ||
            !verificationFailedPopup.pendingResult
        ) {
            resetVerificationFailedPopup();
            return;
        }
        applyManualHolderVerification(
            verificationFailedPopup.holderIndex,
            verificationFailedPopup.pendingResult.token,
            verificationFailedPopup.pendingResult.status
        );
        resetVerificationFailedPopup();
    };

    const clearVerificationState = (idx: number) => {
        setPanStatus((prev) => {
            const next = [...prev];
            next[idx] = DEFAULT_STATUS;
            return next;
        });
        setPanVerifiedFlags((prev) => {
            const next = [...prev];
            next[idx] = false;
            return next;
        });
        setPanVerifyLoading((prev) => {
            const next = [...prev];
            next[idx] = false;
            return next;
        });
    };

    useEffect(() => {
        if (!open) {
            setCloseConfirmOpen(false);
            setPanStatus([]);
            setPanVerifiedFlags([]);
            setPanVerifyLoading([]);
            setInitialSnapshots([]);
            setInitialEditableSnapshots([]);
            setFieldsEnabled([]);
            resetVerificationFailedPopup();
        } else {
            // When popup opens, store initial snapshots of all holders
            const snapshots = initialValues.map(holder => createHolderSnapshot(holder));
            setInitialSnapshots(snapshots);
            const editableSnapshots = initialValues.map(holder => createEditableSnapshot(holder));
            setInitialEditableSnapshots(editableSnapshots);
            // Initialize verification flags and statuses based on existing data
            const flags = initialValues.map(holder => !!holder.panVerificationToken);
            setPanVerifiedFlags(flags);
            const existingStatuses = initialValues.map(holder => holder.panVerificationStatus ?? DEFAULT_STATUS);
            setPanStatus(existingStatuses);

            // Initialize fieldsEnabled: initially all disabled, enable only if verification passed
            const enabled = initialValues.map((holder, index) => {
                const status = existingStatuses[index];
                if (!status) return false;

                // Allow editable fields as long as Aadhaar verification passed
                return status.panAddharCheck;
            });
            setFieldsEnabled(enabled);
        }
    }, [open, initialValues, isBSEEnabled]);

    const initialFormValues: ManualJointHolderFormValues = {
        jointHolders: initialValues.length
            ? initialValues.map(holder => ({
                ...InitialHolder,
                ...holder,
                // Set taxResidentOfAnyCountryOtherThanIndia to true for non-individual applicants (NRI, HUF, etc.)
                taxResidentOfAnyCountryOtherThanIndia: `${onboardingSubType || ''}`.toLowerCase() !== 'individual'
                    ? true
                    : holder.taxResidentOfAnyCountryOtherThanIndia ?? false,
            }))
            : [{
                ...InitialHolder,
                // Set taxResidentOfAnyCountryOtherThanIndia to true for non-individual applicants (NRI, HUF, etc.)
                taxResidentOfAnyCountryOtherThanIndia: `${onboardingSubType || ''}`.toLowerCase() !== 'individual'
                    ? true
                    : false,
            }],
    };

    const handleDialogClose = (_event: Record<string, unknown> | undefined, reason?: string) => {
        if (reason === "backdropClick" || reason === "escapeKeyDown") {
            if (formikDirtyRef.current) {
                setCloseConfirmOpen(true);
                return;
            }
        }
        onClose();
    };

    return (
        <>
            <Formik
                innerRef={formikRef}
                initialValues={initialFormValues}
                validationSchema={validationSchema}
                enableReinitialize
                onSubmit={async (values, actions) => {
                    try {
                        // Process joint holders: clear FATCA fields if taxResidentOfAnyCountryOtherThanIndia is false
                        const handleField = values.jointHolders.map((holder) => ({
                            ...holder,
                            taxCountryName:
                                holder.taxResidentOfAnyCountryOtherThanIndia?.toString() === 'false'
                                    ? ''
                                    : holder.taxCountryName,
                            taxID:
                                holder.taxResidentOfAnyCountryOtherThanIndia?.toString() === 'false'
                                    ? ''
                                    : holder.taxID,
                            idType:
                                holder.taxResidentOfAnyCountryOtherThanIndia?.toString() === 'false'
                                    ? ''
                                    : holder.idType,
                        }));

                        if (mode === 'view') {
                            await onUpdate?.(handleField);
                        } else {
                            await onSave(handleField);
                        }
                    } finally {
                        actions.setSubmitting(false);
                    }
                }}
            >
                {({ values, setFieldValue, dirty, handleSubmit, setFieldTouched, validateForm }) => {
                    formikDirtyRef.current = dirty;
                    formikValuesRef.current = values;
                    const editableChangeFlags = values.jointHolders.map((holder, index) => {
                        const initialSnapshot = initialEditableSnapshots[index] ?? '';
                        return initialSnapshot !== createEditableSnapshot(holder);
                    });
                    const hasEditableChanges = editableChangeFlags.some(Boolean);

                    // Check if all holders are verified (or no edits were made)
                    const allHoldersVerified = values.jointHolders.every((holder, index) => {
                        const currentSnapshot = createHolderSnapshot(holder);
                        const initialSnapshot = initialSnapshots[index] ?? "";
                        const hasBeenEdited = initialSnapshot !== "" && currentSnapshot !== initialSnapshot;
                        const hasInitialData = Boolean(initialSnapshots[index]);
                        const initialToken = initialValues[index]?.panVerificationToken;
                        if (!hasInitialData || !initialToken) {
                            return panVerifiedFlags[index] === true;
                        }
                        if (!hasBeenEdited) {
                            return true;
                        }
                        return panVerifiedFlags[index] === true;
                    });

                    const attemptClose = () => {
                        if (dirty) {
                            setCloseConfirmOpen(true);
                            return;
                        }
                        onClose();
                    };

                    return (
                        <Dialog
                            open={open}
                            onClose={handleDialogClose}
                            fullWidth
                            maxWidth="md"
                            sx={{
                                ".MuiPaper-root": {
                                    borderRadius: 2,
                                },
                            }}
                        >
                            <Form onSubmit={handleSubmit}>
                                <DialogContent>
                                    <FieldArray
                                        name="jointHolders"
                                        render={(arrayHelpers) => (
                                            <Box display="flex" flexDirection="column" gap={3}>
                                                {values.jointHolders.map((holder, index) => {
                                                    const currentSnapshot = createHolderSnapshot(holder);
                                                    const initialSnapshot = initialSnapshots[index] ?? "";
                                                    const hasBeenEdited = initialSnapshot !== "" && currentSnapshot !== initialSnapshot;
                                                    const isVerified = panVerifiedFlags[index] ?? false;
                                                    const statusForIndex = panStatus[index] ?? DEFAULT_STATUS;
                                                    const isLoading = panVerifyLoading[index] ?? false;
                                                    const hasInitialData = Boolean(initialSnapshots[index]);
                                                    const initialToken = initialValues[index]?.panVerificationToken;
                                                    const isExistingHolder = hasInitialData && !!initialToken;
                                                    const areFieldsEnabled = fieldsEnabled[index] ?? false;
                                                    // Fields (fullName, dob, pan) should be non-actionable in view mode
                                                    const isViewMode = isExistingHolder;

                                                    let verifyButtonEnabled = false;
                                                    if (!isExistingHolder) {
                                                        // New holder: allow verify until it succeeds
                                                        verifyButtonEnabled = !isVerified;
                                                    } else if (hasBeenEdited) {
                                                        // Existing holder with edits needs re-verification
                                                        verifyButtonEnabled = !isVerified;
                                                    }
                                                    const buttonDisabled = !verifyButtonEnabled || isLoading;
                                                    const showSuccessIcon = statusForIndex.panAddharCheck && statusForIndex.kraCheck;
                                                    const editableFieldsEnabled = mode === 'view' ? true : areFieldsEnabled;

                                                    const clearVerificationAndToken = () => {
                                                        clearVerificationState(index);
                                                        setFieldValue(
                                                            `jointHolders.${index}.panVerificationToken`,
                                                            '',
                                                            true
                                                        );
                                                        setFieldValue(
                                                            `jointHolders.${index}.panVerificationStatus`,
                                                            null,
                                                            true
                                                        );
                                                        // Disable fields when verification is cleared
                                                        setFieldsEnabled((prev) => {
                                                            const next = [...prev];
                                                            next[index] = false;
                                                            return next;
                                                        });
                                                    };

                                                    const handleVerifyPan = async () => {
                                                        if (mode === 'view') return;
                                                        if (!onVerifyPan) return;
                                                        setPanVerifyLoading((prev) => {
                                                            const next = [...prev];
                                                            next[index] = true;
                                                            return next;
                                                        });
                                                        try {
                                                            const result = await onVerifyPan(index, values.jointHolders[index]);
                                                            if (!result || !result.token) {
                                                                return;
                                                            }
                                                            const status = result.status ?? DEFAULT_STATUS;
                                                            const isAadhaarPassed = Boolean(status.panAddharCheck);
                                                            const isKraPassed = Boolean(status.kraCheck);

                                                            if (!isAadhaarPassed) {
                                                                setVerificationFailedPopup({
                                                                    open: true,
                                                                    message: 'Aadhaar verification failed. Cannot move further.',
                                                                    type: 'aadhaar',
                                                                });
                                                                return;
                                                            }

                                                            if (isBSEEnabled && !isKraPassed) {
                                                                setVerificationFailedPopup({
                                                                    open: true,
                                                                    message: 'KRA verification failed. Do you want to continue?',
                                                                    type: 'kra',
                                                                    holderIndex: index,
                                                                    pendingResult: {
                                                                        token: result.token,
                                                                        status,
                                                                    },
                                                                });
                                                                return;
                                                            }

                                                            applyManualHolderVerification(index, result.token, status);
                                                        } finally {
                                                            setPanVerifyLoading((prev) => {
                                                                const next = [...prev];
                                                                next[index] = false;
                                                                return next;
                                                            });
                                                        }
                                                    };

                                                    return (
                                                        <Box
                                                            key={`holder-${index}`}
                                                            sx={{
                                                                border: "1px solid",
                                                                borderColor: "divider",
                                                                borderRadius: 2,
                                                                p: 3,
                                                            }}
                                                        >
                                                            <Box
                                                                sx={{
                                                                    display: "flex",
                                                                    justifyContent: "space-between",
                                                                    alignItems: "center",
                                                                    mb: 2,
                                                                    color: 'red',
                                                                    height: '10'
                                                                }}
                                                            >
                                                                <Typography variant="body_600" color="text.primary">
                                                                    Joint Holder
                                                                </Typography>
                                                                {values.jointHolders.length > 1 && (
                                                                    <Button
                                                                        variant="text"
                                                                        color="primary"
                                                                        startIcon={<DeleteIcon />}
                                                                        onClick={() => {
                                                                            arrayHelpers.remove(index);
                                                                            clearVerificationState(index);
                                                                        }}

                                                                    >
                                                                        Remove
                                                                    </Button>
                                                                )}
                                                            </Box>
                                                            <Grid container spacing={2}>

                                                                <Grid item xs={12} sm={6}>
                                                                    <MFTextField
                                                                        name={`jointHolders.${index}.fullName`}
                                                                        label="Full Name*"
                                                                        placeholder="Enter Full Name"
                                                                        disabled={isViewMode}
                                                                        startAdornment={
                                                                            <InputAdornment
                                                                                position="start"
                                                                                sx={{
                                                                                    pr: '0 !important',
                                                                                    mx: '0 !important',
                                                                                    borderRight: '0 !important',
                                                                                }}
                                                                            >
                                                                                <MFSelectField
                                                                                    name={`jointHolders.${index}.salutation`}
                                                                                    items={SALUTATION
                                                                                        ?.filter((status: dataTypeMasters) => {
                                                                                            //jointholders are always individual
                                                                                            if (`${status.description ?? ''}`.toLowerCase() === 'm/s.') return;
                                                                                            return status?.description;
                                                                                        })
                                                                                        ?.map((salutation: dataTypeMasters) => ({
                                                                                            key: salutation?.description,
                                                                                            value: salutation?.description,
                                                                                        }))}
                                                                                    disabled={isViewMode}
                                                                                    loading={dataMasterLoading}
                                                                                    sx={(theme: Theme) => ({
                                                                                        '& .MuiOutlinedInput-notchedOutline': {
                                                                                            border: "0 !important"
                                                                                        },
                                                                                        '& .MuiSelect-icon': {
                                                                                            width: '1em !important',
                                                                                            height: '1em !important',
                                                                                        },
                                                                                        "& .MuiSelect-select": {
                                                                                            padding: "0px 32px 0px 0px !important",
                                                                                            borderRadius: "0px !important",
                                                                                            borderRightWidth: "1.3px",
                                                                                            borderRightStyle: "solid",
                                                                                            borderRightColor: theme.palette.text.secondary
                                                                                        },
                                                                                    })}
                                                                                    onChange={(e: any) => {
                                                                                        if (!isViewMode) {
                                                                                            setFieldValue(
                                                                                                `jointHolders.${index}.salutation`,
                                                                                                e.target.value
                                                                                            );
                                                                                            clearVerificationAndToken();
                                                                                        }
                                                                                    }}
                                                                                />
                                                                            </InputAdornment>
                                                                        }
                                                                        onChange={(e: any) => {
                                                                            if (!isViewMode) {
                                                                                setFieldValue(
                                                                                    `jointHolders.${index}.fullName`,
                                                                                    e.target.value
                                                                                );
                                                                                clearVerificationAndToken();
                                                                            }
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <TextDatePicker
                                                                        name={`jointHolders.${index}.dob`}
                                                                        label="Date of Birth*"
                                                                        placeholder="Enter DOB"
                                                                        minDate={getDateValidation({ date: values?.jointHolders?.[index]?.dob, limitValue: yearfor125years }).dateLimitYearsAgo}
                                                                        // maxDate={new Date()}
                                                                        maxDate={getDateValidation({ date: values?.jointHolders?.[index]?.dob, limitValue: eighteenYearsAgoDate }).dateLimitYearsAgo}
                                                                        disabled={isViewMode}
                                                                        customHandleChange={(selectedDate: any) => {
                                                                            if (!isViewMode) {
                                                                                setFieldValue(
                                                                                    `jointHolders.${index}.dob`,
                                                                                    getFormattedISTDate(selectedDate),
                                                                                    true
                                                                                );
                                                                                clearVerificationAndToken();
                                                                            }
                                                                        }}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12}>
                                                                    <Grid container spacing={2} alignItems="center">
                                                                        <Grid item xs={12} sm={7}>
                                                                            <MFTextField
                                                                                name={`jointHolders.${index}.pan`}
                                                                                label="PAN*"
                                                                                placeholder="Enter PAN"
                                                                                disabled={isViewMode}
                                                                                inputProps={{ style: { textTransform: "uppercase" } }}
                                                                                onChange={(e) => {
                                                                                    if (!isViewMode) {
                                                                                        setFieldValue(
                                                                                            `jointHolders.${index}.pan`,
                                                                                            e.target.value.toUpperCase()
                                                                                        );
                                                                                        clearVerificationAndToken();
                                                                                    }
                                                                                }}
                                                                                endAdornment={
                                                                                    isVerified ? (
                                                                                        showSuccessIcon ? (
                                                                                            <CheckCircleIcon sx={{ color: 'success.main' }} />
                                                                                        ) : (
                                                                                            <CancelIcon sx={{ color: 'error.main' }} />
                                                                                        )
                                                                                    ) : undefined
                                                                                }
                                                                            />
                                                                        </Grid>
                                                                        {mode !== 'view' && (
                                                                            <Grid item xs={12} sm={5}>
                                                                                <LoadingButton
                                                                                    variant="outlined"
                                                                                    fullWidth
                                                                                    sx={{ height: 49, color: "primary.main" }}
                                                                                    disabled={buttonDisabled || isViewMode}
                                                                                    loading={isLoading}
                                                                                    onClick={async () => {
                                                                                        await Promise.all([
                                                                                            setFieldTouched(`jointHolders.${index}.firstName`, true, true),
                                                                                            setFieldTouched(`jointHolders.${index}.middleName`, true, true),
                                                                                            setFieldTouched(`jointHolders.${index}.lastName`, true, true),
                                                                                            setFieldTouched(`jointHolders.${index}.dob`, true, true),
                                                                                            setFieldTouched(`jointHolders.${index}.pan`, true, true),
                                                                                        ]);

                                                                                        const formErrors = await validateForm();
                                                                                        const holderErrors = (formErrors as any)?.jointHolders?.[index];
                                                                                        if (
                                                                                            holderErrors?.firstName ||
                                                                                            holderErrors?.middleName ||
                                                                                            holderErrors?.lastName ||
                                                                                            holderErrors?.dob ||
                                                                                            holderErrors?.pan
                                                                                        ) {
                                                                                            return;
                                                                                        }

                                                                                        await handleVerifyPan();
                                                                                    }}
                                                                                >
                                                                                    Verify PAN
                                                                                </LoadingButton>
                                                                            </Grid>
                                                                        )}
                                                                        {isVerified && (
                                                                            <Grid item xs={12}>
                                                                                <Box mt={2} width="100%" display="flex" justifyContent="flex-start" alignItems="flex-start">
                                                                                    <VerifyPanAadharAmlKycSection
                                                                                        verifyPanApiCalled
                                                                                        onboardingType="individual"
                                                                                        checkStatus={statusForIndex}
                                                                                    />
                                                                                </Box>
                                                                            </Grid>
                                                                        )}
                                                                    </Grid>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.gender`}
                                                                        label="Gender*"
                                                                        items={GENDER.map((relation: dataTypeMasters) => ({
                                                                            key: relation?.description,
                                                                            value: relation?.description,
                                                                        }))}
                                                                        disabled={!editableFieldsEnabled}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFTextField
                                                                        name={`jointHolders.${index}.placeOfBirth`}
                                                                        label="Place of Birth*"
                                                                        placeholder="Enter Place of Birth"
                                                                        disabled={!editableFieldsEnabled}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.countryOfBirth`}
                                                                        label="Country of Birth*"
                                                                        items={COUNTRY.map((relation: dataTypeMasters) => ({
                                                                            key: relation?.description,
                                                                            value: relation?.description,
                                                                        }))}
                                                                        disabled={!editableFieldsEnabled || dataMasterLoading}
                                                                        loading={dataMasterLoading}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.grossAnnualIncome`}
                                                                        label="Gross Annual Income*"
                                                                        items={INCOME_SLAB?.map((obj: dataTypeMasters) => ({
                                                                            key: obj?.description,
                                                                            value: obj?.description
                                                                        }))}
                                                                        sx={{
                                                                            "&.MuiOutlinedInput-root": {
                                                                                "& fieldset>legend": {
                                                                                    fontSize: "0.78em",
                                                                                }
                                                                            },
                                                                            '& .MuiSelect-icon': {
                                                                                width: '1em !important',
                                                                                height: '1em !important',
                                                                            },
                                                                        }}
                                                                        loading={dataMasterLoading}
                                                                        disabled={!editableFieldsEnabled || dataMasterLoading}

                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.politicallyExposedPersonStatus`}
                                                                        label="Politically Exposed Person (PEP) Status"
                                                                        items={PEP_STATUS.map((pep: dataTypeMasters) => ({
                                                                            key: pep?.description,
                                                                            value: pep?.description
                                                                        }))}
                                                                        loading={dataMasterLoading}
                                                                        disabled={!editableFieldsEnabled || dataMasterLoading}
                                                                    />
                                                                </Grid>
                                                                <Grid item lg={2} sx={{ display: { xs: 'none', lg: 'block' } }} />
                                                                <Grid item xs={12} lg={6}>
                                                                    <UseRadioGroup
                                                                        formLabel={`Tax Resident of any country other than India`}
                                                                        name={`jointHolders.${index}.taxResidentOfAnyCountryOtherThanIndia`}
                                                                        items={[
                                                                            { label: 'Yes', value: 'true' },
                                                                            { label: 'No', value: 'false' },
                                                                        ]}
                                                                        value={holder.taxResidentOfAnyCountryOtherThanIndia?.toString()}
                                                                        disabled={!editableFieldsEnabled || `${onboardingSubType || ''}`.toLowerCase() !== 'individual'}
                                                                    />
                                                                </Grid>
                                                                <Grid item lg={6} sx={{ display: { xs: 'none', lg: 'block' } }} />
                                                                {holder?.taxResidentOfAnyCountryOtherThanIndia?.toString() === 'true' && (
                                                                    <>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <MFSelectField
                                                                                name={`jointHolders.${index}.taxCountryName`}
                                                                                label={`Tax country Name *`}
                                                                                items={COUNTRY.map((nationality: dataTypeMasters) => ({
                                                                                    key: nationality?.description,
                                                                                    value: nationality?.description,
                                                                                }))}
                                                                                disabled={!editableFieldsEnabled || dataMasterLoading}
                                                                                loading={dataMasterLoading}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <MFTextField
                                                                                name={`jointHolders.${index}.taxID`}
                                                                                label={`Tax ID *`}
                                                                                placeholder="Enter Tax ID"
                                                                                disabled={!editableFieldsEnabled}
                                                                            />
                                                                        </Grid>
                                                                        <Grid item xs={12} sm={6}>
                                                                            <MFSelectField
                                                                                name={`jointHolders.${index}.idType`}
                                                                                label={`ID Type *`}
                                                                                items={FATCA_ID_TYPE?.map((idType: dataTypeMasters) => ({
                                                                                    key: idType.description,
                                                                                    value: idType.description,
                                                                                }))}
                                                                                disabled={!editableFieldsEnabled}
                                                                                isCapitalize
                                                                            />
                                                                        </Grid>
                                                                    </>
                                                                )}
                                                                {/* //invisible grids to maintain structure when it is true or false */}
                                                                {holder?.taxResidentOfAnyCountryOtherThanIndia?.toString() !== 'true' && (
                                                                    <>
                                                                        <Grid item xs={12} sm={6} sx={{ height: 0, visibility: 'hidden' }} />
                                                                        <Grid item xs={12} sm={6} sx={{ height: 0, visibility: 'hidden' }} />
                                                                        <Grid item xs={12} sm={6} sx={{ height: 0, visibility: 'hidden' }} />
                                                                    </>
                                                                )}


                                                                <Grid item xs={12} sm={6} sx={{ display: { xs: 'none', sm: 'block' } }}>
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFTextField
                                                                        name={`jointHolders.${index}.email`}
                                                                        label="Email ID*"
                                                                        placeholder="Enter Email ID"
                                                                        disabled={!editableFieldsEnabled}
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.emailPersonRelation`}
                                                                        label="This Email belongs to?*"
                                                                        items={emailRelationOptions.map((relation: dataTypeMasters) => ({
                                                                            key: relation?.description,
                                                                            value: relation?.description,
                                                                        }))}
                                                                        disabled={!editableFieldsEnabled}
                                                                    />
                                                                </Grid>
                                                                {`${holder.emailPersonRelation ?? ""}`.toLowerCase() === "others" && (
                                                                    <Grid item xs={12} sm={6}>
                                                                        <MFTextField
                                                                            name={`jointHolders.${index}.emailDeclarationOther`}
                                                                            label="Specify Relationship*"
                                                                            placeholder="Enter Relationship"
                                                                            disabled={!editableFieldsEnabled}
                                                                        />
                                                                    </Grid>
                                                                )}
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFTextField
                                                                        name={`jointHolders.${index}.mobile`}
                                                                        label="Mobile Number*"
                                                                        placeholder="Enter Mobile Number"
                                                                        disabled={!editableFieldsEnabled}
                                                                        onKeyDown={(e) => {
                                                                            preventSpecialCharacters(e);
                                                                        }}
                                                                        startAdornment={
                                                                            <CountryCodesDropDown
                                                                                name={`jointHolders.${index}.countryNameAndCode`}
                                                                                value={holder.countryNameAndCode || "India: +91"}
                                                                                disabled={!areFieldsEnabled}
                                                                            />
                                                                        }
                                                                    />
                                                                </Grid>
                                                                <Grid item xs={12} sm={6}>
                                                                    <MFSelectField
                                                                        name={`jointHolders.${index}.phonePersonRelation`}
                                                                        label="This Mobile belongs to?*"
                                                                        items={mobileRelationOptions.map((relation: dataTypeMasters) => ({
                                                                            key: relation?.description,
                                                                            value: relation?.description,
                                                                        }))}
                                                                        disabled={!editableFieldsEnabled}
                                                                    />
                                                                </Grid>
                                                                {`${holder.phonePersonRelation ?? ""}`.toLowerCase() === "others" && (
                                                                    <Grid item xs={12} sm={6}>
                                                                        <MFTextField
                                                                            name={`jointHolders.${index}.mobileDeclarationOther`}
                                                                            label="Specify Relationship*"
                                                                            placeholder="Enter Relationship"
                                                                            disabled={!editableFieldsEnabled}
                                                                        />
                                                                    </Grid>
                                                                )}
                                                            </Grid>
                                                        </Box>
                                                    )
                                                })}
                                                {/* <Button
                                                    variant="outlined"
                                                    startIcon={<AddIcon />}
                                                    sx={{ alignSelf: "flex-start", color: "primary.main" }}
                                                    onClick={() => arrayHelpers.push({ ...InitialHolder })}
                                                >
                                                    Add Another Joint Holder
                                                </Button> */}
                                            </Box>
                                        )}
                                    />
                                </DialogContent>
                                <Divider />
                                <DialogActions
                                    sx={{
                                        px: 3,
                                        py: 2,
                                        justifyContent: "center",
                                        gap: 2,
                                    }}
                                >
                                    <Box sx={{ display: 'flex', gap: 2, minWidth: 200 }}>
                                        {mode === 'view' ? (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    onClick={attemptClose}
                                                    sx={{ color: "primary.main", minWidth: 100 }}
                                                >
                                                    Close
                                                </Button>
                                                <Button
                                                    type="submit"
                                                    variant="contained"
                                                    disabled={!hasEditableChanges}
                                                    sx={{ minWidth: 100 }}
                                                >
                                                    Update
                                                </Button>
                                            </>
                                        ) : (
                                            <>
                                                <Button
                                                    variant="outlined"
                                                    onClick={attemptClose}
                                                    sx={{ color: "primary.main", minWidth: 200 }}
                                                    disabled={isSaving}
                                                >
                                                    Close
                                                </Button>
                                                <LoadingButton
                                                    type="submit"
                                                    variant="contained"
                                                    loading={isSaving}
                                                    disabled={!allHoldersVerified || isSaving}
                                                    sx={{ minWidth: 200 }}
                                                >
                                                    Save
                                                </LoadingButton>
                                            </>
                                        )}
                                    </Box>
                                </DialogActions>
                            </Form>
                        </Dialog>
                    );
                }}
            </Formik>
            <Dialog
                open={closeConfirmOpen}
                onClose={() => setCloseConfirmOpen(false)}
                maxWidth="xs"
                fullWidth
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: 2,
                    },
                }}
            >
                <DialogContent>
                    <Typography variant="body_600" color="text.primary">
                        Details cannot be saved.
                    </Typography>
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 3, pt: 1 }}>
                    <Button variant="outlined" onClick={() => setCloseConfirmOpen(false)} sx={{ color: "primary.main" }}>
                        Cancel
                    </Button>
                    <Button
                        variant="contained"
                        onClick={() => {
                            setCloseConfirmOpen(false);
                            if (formikRef.current) {
                                formikRef.current.resetForm();
                            }
                            setPanStatus([]);
                            setPanVerifiedFlags([]);
                            setPanVerifyLoading([]);
                            setInitialSnapshots([]);
                            onClose();
                        }}
                    >
                        Ok
                    </Button>
                </DialogActions>
            </Dialog>
            <Dialog
                open={verificationFailedPopup.open}
                onClose={handleVerificationFailureClose}
                maxWidth="xs"
                fullWidth
                sx={{
                    ".MuiPaper-root": {
                        borderRadius: 2,
                    },
                }}
            >
                {verificationFailedPopup.type === 'kra' ? (
                    <>
                        <DialogContent sx={{ paddingTop: "25px" }}>
                            <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                                <CardMedia
                                    component="img"
                                    src="/images/warning-icon-red.svg"
                                    sx={{ width: '24px', height: '24px', mr: 2 }}
                                />
                                <Typography variant="h2" color="text.primary">
                                    KRA Verification Failed
                                </Typography>
                            </Box>
                            <Typography variant="subtitle_400" color="text.secondary">
                                {verificationFailedPopup.message || 'Your KRA is not verified.'}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                            <Button variant="contained" onClick={handleVerificationFailureClose}>
                                Close
                            </Button>
                            <Button variant="contained" onClick={handleKraFailureProceed}>
                                Ok
                            </Button>
                        </DialogActions>
                    </>
                ) : (
                    <>
                        <DialogContent>
                            <Typography variant="body_600" color="text.primary">
                                {verificationFailedPopup.message}
                            </Typography>
                        </DialogContent>
                        <DialogActions sx={{ px: 3, pb: 3, pt: 1, gap: 1.5 }}>
                            <Button variant="contained" onClick={handleVerificationFailureClose}>
                                Ok
                            </Button>
                        </DialogActions>
                    </>
                )}
            </Dialog>
        </>
    );
};

export default FreeTextPopup;

